import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { BookOpen, Search, ArrowRight, TrendingUp, Sparkles } from "lucide-react";

export default function HelpKb() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [recent, setRecent] = useState([]);
  const [popular, setPopular] = useState([]);
  const [search, setSearch] = useState(params.get("q") || "");

  useEffect(() => {
    (async () => {
      try {
        const [cats, recentRes, popularRes] = await Promise.all([
          api.get("/kb/categories"),
          api.get("/kb/articles", { params: { limit: 5 } }),
          api.get("/kb/articles", { params: { limit: 5 } }),  // backend sorts by published_at; popular sort handled below
        ]);
        setCategories(cats.data.items || []);
        setRecent(recentRes.data.items || []);
        // crude popular sort by views client-side (small dataset).
        const sorted = [...(popularRes.data.items || [])].sort((a, b) => (b.views || 0) - (a.views || 0));
        setPopular(sorted.slice(0, 5));
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    const q = search.trim();
    if (q.length < 2) return;
    navigate(`/help/kb/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <AppShell title="Knowledge base">
      <div className="max-w-5xl mx-auto py-8 px-4">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-50 text-orange-700 text-xs font-semibold mb-3">
            <BookOpen className="w-3.5 h-3.5" /> EntryBook help
          </div>
          <h1 className="text-3xl font-semibold text-navy-900 dark:text-white" data-testid="kb-heading">
            How can we help?
          </h1>
          <p className="text-sm text-navy-500 mt-2 max-w-xl mx-auto">
            Search the knowledge base for how-to guides, or browse by category. Still stuck? Submit a ticket and we'll come back to you.
          </p>
          <form onSubmit={onSearch} className="mt-6 flex max-w-xl mx-auto">
            <div className="flex-1 flex items-center gap-2 bg-white dark:bg-navy-900 border border-navy-200 rounded-l-lg px-3 focus-within:ring-2 focus-within:ring-orange-300">
              <Search className="w-4 h-4 text-navy-400" />
              <input
                type="search"
                placeholder="Search articles…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 py-2 bg-transparent outline-none text-sm"
                data-testid="kb-search-input"
              />
            </div>
            <button
              type="submit"
              className="px-5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-r-lg"
              data-testid="kb-search-submit"
            >
              Search
            </button>
          </form>
        </div>

        <h2 className="text-sm uppercase tracking-wider text-navy-500 font-semibold mb-3">
          Browse by category
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10" data-testid="kb-category-grid">
          {categories.map((c) => (
            <Link
              key={c.id}
              to={`/help/kb/c/${c.slug}`}
              className="eb-card p-5 hover:border-orange-300 transition-colors flex items-start gap-3"
              data-testid={`kb-category-${c.slug}`}
            >
              <div className="w-10 h-10 rounded-lg bg-orange-50 dark:bg-orange-900/30 flex items-center justify-center shrink-0">
                <BookOpen className="w-5 h-5 text-orange-600" />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-navy-900 dark:text-white">{c.name}</div>
                <div className="text-xs text-navy-500 mt-0.5">{c.description}</div>
                <div className="text-[11px] text-navy-400 mt-2">{c.article_count} article{c.article_count === 1 ? "" : "s"}</div>
              </div>
              <ArrowRight className="w-4 h-4 text-navy-400 mt-1" />
            </Link>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ArticleList title="Recently updated" icon={<Sparkles className="w-3.5 h-3.5" />} items={recent} testid="kb-recent" />
          <ArticleList title="Popular" icon={<TrendingUp className="w-3.5 h-3.5" />} items={popular} testid="kb-popular" />
        </div>
      </div>
    </AppShell>
  );
}

function ArticleList({ title, items, icon, testid }) {
  return (
    <div className="eb-card p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-navy-500 font-semibold mb-3">
        {icon} {title}
      </div>
      <ul className="space-y-1.5">
        {items.length === 0 ? (
          <li className="text-xs text-navy-400">No articles yet.</li>
        ) : items.map((a) => (
          <li key={a.id}>
            <Link
              to={`/help/kb/${a.slug}`}
              className="text-sm text-navy-700 dark:text-navy-200 hover:text-orange-600"
              data-testid={`kb-article-link-${a.slug}`}
            >
              {a.title}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
